import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { enforceSlash } from '../../utils/helpers';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import SimpleTable from '../../components/super/SimpleTable';
import { Fab } from '@material-ui/core';
import { Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { runInAction } from 'mobx';
import NumberInput from '../../components/input/NumberInput';

const useStyles = makeStyles((theme) => ({
    fab: {
        position: 'fixed',
        bottom: theme.spacing(4),
        right: theme.spacing(4),
    },
}));

function Questions() {
    const { superStore, commonStore } = useStores();
    const {
        loadingQuestions,
        questions,
        sortedQuestions,
        questionsError,
        updatingQuestionsMaxScore,
        questionsMaxScoreUpdated,
    } = superStore;
    const { pathname } = useLocation();
    const baseUrl = enforceSlash(pathname);
    const navigate = useNavigate();
    const classes = useStyles();

    const loadData = async () => {
        superStore.loadQuestions();
    };

    const updateQuestionsMaxScore = async () => {
        await superStore.updateQuestionsMaxScore({});
        if (questionsMaxScoreUpdated) {
            commonStore.success('Max scores were updated successfully');
        }
    };

    const reorderQuestions = () => {
        runInAction(() => {
            superStore.reorderingQuestions = true;
            superStore.questions = superStore.sortedQuestions
        });

        setTimeout(() => {
            runInAction(() => {
                superStore.reorderingQuestions = false;
            });
        }, 0);
    }

    const updateOrder = async (questionId, orderNumber) => {
        superStore.updateQuestionOrder(questionId, orderNumber);
        await superStore.debouncedSaveQuestionOrder(
            questionId,
            orderNumber
        );
    };

    useEffect(() => {
        loadData();
    }, []);

    const columns = React.useMemo(
        () => [
            {
                Header: 'Id',
                accessor: 'id',
                Cell: (x) => (
                    <Link to={`${baseUrl}${x.value}/edit`}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: 'Native Name',
                accessor: 'name',
                Cell: (x) => (
                    <Link
                        to={`${baseUrl}${x.row.original.id}/edit`}
                    >
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: '# of Rules',
                accessor: 'rulesCount',
            },
            {
                Header: 'Public',
                accessor: 'isPublic',
                Cell: (x) =>
                    x.value > 0 ? (
                        <DoneIcon color="primary" />
                    ) : (
                        <ClearIcon color="secondary" />
                    ),
            },
            {
                Header: 'Code',
                accessor: 'external_id',
            },
            {
                Header: 'Max Score',
                accessor: 'max_score',
            },
            {
                Header: 'Difficulty',
                accessor: 'difficulty',
            },
            {
                Header: 'Order',
                accessor: 'order_number',
                textAlign: 'center',
                Cell: observer((x) => {
                    let value = superStore.questions.find(
                        (question) => question.id === x.row.original.id
                    ).order_number;

                    return (
                        <NumberInput
                            value={value}
                            onChange={(orderNumber) =>
                                updateOrder(x.row.original.id, orderNumber)
                            }
                            min={0}
                            step={1}
                        />
                    );
                }),
            },
        ],
        []
    );

    return (
        <Grid
            container
            direction="row"
            justify="center"
            align="center"
            spacing={2}
        >
            <Grid item xs={12}>
                <Box mt={4}>
                    <Typography variant="h2">Questions</Typography>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box mb={4}>
                    {updatingQuestionsMaxScore ? (
                        <ThemedSpinner />
                    ) : (
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={updateQuestionsMaxScore}
                        >
                            Update questions max score
                        </Button>
                    )}
                </Box>
            </Grid>
            <Tooltip title="Create question" aria-label="add" placement="left">
                <Fab
                    color="primary"
                    onClick={() => navigate('/content/questions/create')}
                    className={classes.fab}
                >
                    <AddIcon />
                </Fab>
            </Tooltip>
            {loadingQuestions ? (
                <ThemedSpinner />
            ) : questions && questions.length > 0 ? (
                <Grid item xs={12}>
                    <SimpleTable
                        columns={columns}
                        data={sortedQuestions}
                        needsFilter={true}
                        extraAction={
                            <Button
                                onClick={reorderQuestions}
                                color="primary"
                                variant="contained"
                            >
                                Reorder
                            </Button>
                        }
                    />
                </Grid>
            ) : (
                <Grid item xs={12}>
                    No questions found :(
                </Grid>
            )}
            {questionsError && (
                <Grid item>
                    <ErrorMessage error={questionsError} />
                </Grid>
            )}
        </Grid>
    );
}

export default observer(Questions);
